import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Logo, KenyaStripe } from "@/components/Logo";
import { ArrowLeft, Trophy, Radio } from "lucide-react";
import ShareButton from "@/components/ShareButton";
import WildCardReport from "@/components/WildCardReport";

const DELTA_FLASH_MS = 6000;

export default function PublicStandings() {
  const [sp] = useSearchParams();
  const highlightId = sp.get("highlight") || null;
  const highlightRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // deltas: { [team_id]: { gd: +3, for: +6, against: +2, ts: Date.now() } }
  const [deltas, setDeltas] = useState({});
  const [liveConnected, setLiveConnected] = useState(false);
  const prevRowsRef = useRef({});
  const esRef = useRef(null);
  const scrolledOnceRef = useRef(false);

  // After the standings load, scroll the highlighted row into view once.
  useEffect(() => {
    if (!highlightId || scrolledOnceRef.current || rows.length === 0) return;
    if (highlightRef.current) {
      // Defer to next frame so layout is settled.
      requestAnimationFrame(() => {
        highlightRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      scrolledOnceRef.current = true;
    }
  }, [highlightId, rows]);

  // Apply a fresh rows array — compute per-team diffs vs the previous snapshot
  // and flash the delta chips for a few seconds.
  const applyRows = (fresh) => {
    const nextDeltas = { ...deltas };
    let anyDelta = false;
    for (const r of fresh) {
      const prev = prevRowsRef.current[r.id];
      if (prev) {
        const dGD = (r.game_diff ?? 0) - (prev.game_diff ?? 0);
        const dFor = (r.for ?? 0) - (prev.for ?? 0);
        const dAg = (r.against ?? 0) - (prev.against ?? 0);
        const dWon = (r.won ?? 0) - (prev.won ?? 0);
        if (dGD !== 0 || dFor !== 0 || dAg !== 0 || dWon !== 0) {
          nextDeltas[r.id] = { gd: dGD, for: dFor, against: dAg, won: dWon, ts: Date.now() };
          anyDelta = true;
        }
      }
      prevRowsRef.current[r.id] = { ...r };
    }
    setRows(fresh);
    if (anyDelta) {
      setDeltas(nextDeltas);
      // Auto-expire each delta after DELTA_FLASH_MS.
      window.setTimeout(() => {
        setDeltas((cur) => {
          const cleaned = {};
          for (const [k, v] of Object.entries(cur)) {
            if (Date.now() - v.ts < DELTA_FLASH_MS) cleaned[k] = v;
          }
          return cleaned;
        });
      }, DELTA_FLASH_MS + 100);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/standings");
        applyRows(data || []);
      } catch (e) {
        console.error("[PublicStandings] Failed to load standings", e);
      }
      setLoading(false);
    })();
    // SSE — refresh on any standings-affecting event.
    try {
      const url = `${API_BASE}/api/public/standings/stream`;
      const es = new EventSource(url);
      esRef.current = es;
      es.onopen = () => setLiveConnected(true);
      es.onerror = () => setLiveConnected(false);
      es.addEventListener("standings", (evt) => {
        try {
          const payload = JSON.parse(evt.data);
          if (Array.isArray(payload)) applyRows(payload);
          else if (payload?.rows) applyRows(payload.rows);
        } catch {
          /* ignore malformed */
        }
      });
      es.onmessage = (evt) => {
        // Some SSE payloads may come through the default channel — try to parse.
        try {
          const payload = JSON.parse(evt.data);
          if (Array.isArray(payload)) applyRows(payload);
          else if (payload?.rows) applyRows(payload.rows);
        } catch {
          /* ignore heartbeats */
        }
      };
    } catch (e) {
      // Fallback: poll every 15s if EventSource is unavailable.
      const t = setInterval(async () => {
        try {
          const { data } = await api.get("/public/standings");
          applyRows(data || []);
        } catch {
          /* noop */
        }
      }, 15000);
      return () => clearInterval(t);
    }
    return () => {
      esRef.current?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#060d09] text-white">
      <div className="kenya-stripe" />
      <header className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/"><Logo variant="inline" className="h-10" /></Link>
        <nav className="hidden md:flex items-center gap-5 text-xs uppercase tracking-widest text-zinc-400">
          <Link to="/" className="hover:text-white">Home</Link>
          <Link to="/players-directory" className="hover:text-white">Players</Link>
          <Link to="/live" className="hover:text-white">Live</Link>
        </nav>
      </header>

      <section className="max-w-5xl mx-auto px-6 pt-8 pb-16" data-testid="public-standings-page">
        <Link to="/" className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white inline-flex items-center mb-3" data-testid="standings-back-home"><ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to Home</Link>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#00A86B] font-bold flex items-center gap-2">
          League Table
          {liveConnected && (
            <span className="inline-flex items-center gap-1 text-red-300" data-testid="standings-live-indicator">
              <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" /> LIVE
            </span>
          )}
        </div>
        <h1 className="font-anton text-6xl md:text-7xl uppercase tracking-tighter" data-testid="standings-title">Standings.</h1>
        <KenyaStripe className="mt-3 w-24" />
        <div className="mt-4">
          <ShareButton testId="share-standings-btn" label="Share Standings"
            title="League Standings · Mara Padel League S3"
            url={`${API_BASE}/share/standings`} />
        </div>

        {loading ? (
          <div className="text-zinc-400 mt-10">Loading…</div>
        ) : rows.length === 0 ? (
          <Card className="p-12 text-center bg-[#11241b] border-white/10 border-dashed mt-8" data-testid="standings-empty">
            <Trophy className="w-10 h-10 mx-auto text-zinc-600 mb-3" />
            <div className="font-anton text-2xl uppercase text-white">No Results Yet</div>
            <p className="text-sm text-zinc-400 mt-2">Standings will populate as matches finish in Season 03.</p>
          </Card>
        ) : (
          <Card className="bg-[#11241b] border-white/10 overflow-hidden mt-8">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="standings-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
                    <th className="px-4 py-3 w-12">#</th>
                    <th className="px-4 py-3">Team</th>
                    <th className="px-4 py-3 w-14 text-center" title="Team ties played">P</th>
                    <th className="px-4 py-3 w-24 text-right" title="Game Difference — sole Season 3 ranking metric for the group stage">GD</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const gd = r.game_diff ?? (r.for - r.against);
                    const delta = deltas[r.id];
                    const rowFlash =
                      delta && Date.now() - delta.ts < DELTA_FLASH_MS
                        ? "bg-[#F4B400]/10 animate-pulse-slow"
                        : "";
                    const isHighlighted = highlightId === r.id;
                    return (
                    <tr
                      key={r.id}
                      ref={isHighlighted ? highlightRef : null}
                      className={`border-b border-white/5 transition-colors duration-500 ${i === 0 ? "bg-[#00A86B]/5" : ""} ${rowFlash} ${
                        isHighlighted
                          ? "bg-[#F4B400]/15 ring-2 ring-[#F4B400] ring-inset shadow-[0_0_24px_rgba(244,180,0,0.35)]"
                          : ""
                      }`}
                      data-testid={`standings-row-${r.id}`}
                      data-highlighted={isHighlighted ? "true" : undefined}
                    >
                      <td className="px-4 py-3 font-anton text-xl text-zinc-300">{i + 1}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center font-anton text-sm text-white" style={{ background: r.color || "#16322a", border: "1px solid rgba(255,255,255,0.1)" }}>
                            {(r.name || "?").slice(0, 2).toUpperCase()}
                          </div>
                          <Link to={`/teams/${r.id}`} className="text-white font-bold hover:text-[#00A86B]" data-testid={`standings-team-${r.id}`}>{r.name}</Link>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-zinc-300">{r.played}</td>
                      <td className={`px-4 py-3 text-right font-anton text-2xl tabular-nums ${gd > 0 ? "text-emerald-300" : gd < 0 ? "text-red-300" : "text-white"}`}>
                        {gd > 0 ? "+" : ""}{gd}
                        {delta && delta.gd !== 0 && (
                          <span
                            className={`ml-1.5 text-[10px] uppercase font-bold px-1.5 py-0.5 rounded align-middle ${
                              delta.gd > 0 ? "bg-emerald-500/25 text-emerald-200" : "bg-red-500/25 text-red-200"
                            }`}
                            data-testid={`standings-delta-gd-${r.id}`}
                          >
                            {delta.gd > 0 ? "+" : ""}{delta.gd}
                          </span>
                        )}
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          </Card>
        )}
        <div className="mt-6" data-testid="wildcard-report-slot">
          <WildCardReport />
        </div>
      </section>
      <div className="kenya-stripe" />
    </div>
  );
}
